import React, { useEffect, useState } from 'react';
import { Form, InputNumber, Select, Typography, message } from 'antd';

import { listTipos, type TipoDePassatempo } from '../../../services/admin';
import type { NewPuzzleConfig, NewPuzzleParams } from './types';

// Campos de criação de um jogo novo, declarados pelo `newPuzzle` do módulo (tipo e grade),
// e a montagem dos `NewPuzzleParams`. Quem usa é o "+ Novo jogo" (catalog/NewGameWizard),
// que navega para a rota de criação passando os params por router state; daí o shell chama
// module.createEmpty — criar e editar viram o mesmo fluxo.
//
// Grade: NADA aqui sabe que um tipo tem grade. O módulo declara `dimensions` e este
// arquivo obedece — sem `dimensions` os campos somem (rébus), `fixed` os mostra
// travados (sudoku 9 × 9) e `author` usa o [min, max] do módulo.

export interface NewPuzzleFormValues {
  titulo: string;
  /** Ausentes quando o módulo não declara `dimensions` (tipo sem grade). */
  largura?: number;
  altura?: number;
  /** Ausente quando o módulo não declara o campo "Tipo" (caso dos jogos). */
  tiposDePassatemposId?: number;
}

/** Valores iniciais dos campos extras (grade e tipo) que o módulo declara. */
export function newPuzzleInitialValues(config: NewPuzzleConfig): Partial<NewPuzzleFormValues> {
  return {
    largura: config.dimensions?.largura,
    altura: config.dimensions?.altura,
    tiposDePassatemposId: config.tipo?.defaultId,
  };
}

/** Monta os `NewPuzzleParams` a partir do formulário e da declaração do módulo. */
export function buildNewPuzzleParams(
  config: NewPuzzleConfig,
  values: NewPuzzleFormValues,
): NewPuzzleParams {
  // Em `fixed` o tamanho é do TIPO: o valor sai da DECLARAÇÃO do módulo, não do
  // formulário. O campo desabilitado é só a affordance; a garantia é esta linha.
  const fixed = config.dimensions?.mode === 'fixed' ? config.dimensions : undefined;
  return {
    titulo: values.titulo.trim(),
    // Tipo sem grade não tem o que coletar: `0 × 0` é o contrato de `NewPuzzleParams`
    // (o `createEmpty` de quem não declara `dimensions` ignora os dois campos).
    largura: fixed?.largura ?? values.largura ?? 0,
    altura: fixed?.altura ?? values.altura ?? 0,
    tiposDePassatemposId: values.tiposDePassatemposId ?? null,
  };
}

interface NewPuzzleFieldsProps {
  config: NewPuzzleConfig;
  /** Recarrega a lista de tipos quando muda para `true` (o passo abrindo). */
  active: boolean;
  /** Rótulo do campo de `tiposDePassatemposId`. */
  tipoLabel?: string;
  /** Texto de ajuda do campo de tipo. `null` = sem texto. */
  tipoExtra?: string | null;
}

/**
 * Os campos EXTRAS de criação que o módulo declara (tipo e grade), como `Form.Item`s
 * — vivem dentro de um `Form` de quem usa (o "+ Novo jogo").
 */
export const NewPuzzleFields: React.FC<NewPuzzleFieldsProps> = ({
  config,
  active,
  tipoLabel = 'Tipo',
  tipoExtra = 'Obrigatório: passatempo sem tipo some dos filtros do catálogo.',
}) => {
  const [tipos, setTipos] = useState<TipoDePassatempo[]>([]);
  const tipoField = config.tipo;
  const dimensions = config.dimensions;
  // Só o modo `author` tem limites — é também o único em que o campo é editável.
  const limits = dimensions?.mode === 'author' ? dimensions : undefined;

  useEffect(() => {
    if (!active || !tipoField) return;
    listTipos()
      .then((all) => setTipos(all.filter((t) => tipoField.filter(t))))
      .catch(() => message.error('Falha ao carregar os tipos.'));
  }, [active, tipoField]);

  return (
    <>
      {tipoField && (
        <Form.Item
          name="tiposDePassatemposId"
          label={tipoLabel}
          rules={[{ required: true, message: `Escolha o ${tipoLabel.toLowerCase()}.` }]}
          extra={tipoExtra ?? undefined}
        >
          <Select placeholder={tipoLabel}>
            {tipos.map((t) => (
              <Select.Option key={t.id} value={t.id}>
                {t.nomeFantasia ?? t.nome ?? `#${t.id}`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {dimensions && (
        <>
          <Form.Item name="largura" label="Largura" rules={[{ required: true }]}>
            <InputNumber min={limits?.min} max={limits?.max} disabled={!limits} />
          </Form.Item>
          <Form.Item name="altura" label="Altura" rules={[{ required: true }]}>
            <InputNumber min={limits?.min} max={limits?.max} disabled={!limits} />
          </Form.Item>
          {!limits && (
            <Typography.Text type="secondary">
              O tamanho deste tipo é fixo — não é escolhido na criação.
            </Typography.Text>
          )}
        </>
      )}
    </>
  );
};
